import { useEffect, useState } from 'react'
import { Dialog, DialogContent, DialogTitle } from './ui/dialog'
import { Button } from './ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  useGetTimesheetMutation,
  type TimesheetResponse,
  type TimesheetEntry,
} from '@/redux/service/employee'
import { Label } from './ui/label'
import { Calendar } from './ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover'
import { format } from 'date-fns'
import { cn } from '@/lib/utils'
import {
  Clock,
  Calendar as CalendarIcon,
  User as UserIcon,
  Tag as TagIcon,
  CheckCircle2,
  XCircle,
  FileText,
  AlertCircle,
} from 'lucide-react'
import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'
import toast from 'react-hot-toast'

dayjs.extend(duration)

interface EmloyeeModeProps {
  isOpen: boolean
  isClose: () => void
  id: string | number | undefined
}

const EmloyeeMode = ({ isOpen, isClose, id }: EmloyeeModeProps) => {
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [showTable, setShowTable] = useState(false)
  const [timeentries, setTimeentries] = useState<TimesheetResponse | null>(null)
  const [getTimesheet, { isLoading }] = useGetTimesheetMutation()

  const getTimestamps = (startDate: string, endDate: string) => {
    if (!startDate || !endDate) return undefined
    const start = dayjs(startDate).startOf('day').add(1, 'hour')
    const end = dayjs(endDate).endOf('day')

    return {
      startTimestamp: start.valueOf(),
      endTimestamp: end.valueOf(),
    }
  }

  const handleSubmit = async () => {
    if (!startDate || !endDate) return
    const timestamps = getTimestamps(startDate, endDate)
    if (!timestamps) return
    if (!id) return
    const { startTimestamp, endTimestamp } = timestamps
    const data = { start: startTimestamp, end: endTimestamp, userId: id }
    try {
      const result = await getTimesheet({ data }).unwrap()
      setTimeentries(result)
      setShowTable(true)
    } catch (error: unknown) {
      const err = error as { data?: { message?: string } }
      toast.error(err?.data?.message ?? 'Something went wrong')
    }
  }

  const formatDuration = (ms: string | number) => {
    const msNum = Number(ms)
    if (Number.isNaN(msNum) || msNum <= 0) return '0h 0m'
    const dur = dayjs.duration(msNum)
    const hours = Math.floor(dur.asHours())
    const minutes = dur.minutes()
    return `${hours}h ${minutes}m`
  }

  const formatDateTime = (timestamp: string | number) => {
    if (!timestamp) return '-'
    return dayjs(Number(timestamp)).format('MMM D, h:mm A')
  }

  const entries = timeentries?.data || []

  useEffect(() => {
    if (!isOpen) {
      setTimeentries(null)
      setShowTable(false)
      setEndDate('')
      setStartDate('')
    }
  }, [isOpen])

  return (
    <Dialog open={isOpen} onOpenChange={isClose}>
      <DialogContent className='max-w-[95vw] md:max-w-6xl max-h-[90vh] flex flex-col p-0 overflow-hidden bg-background border-border rounded-xl shadow-2xl'>
        {/* HEADER */}
        <div className='px-8 py-6 border-b border-border flex items-center justify-between bg-muted/30'>
          <div className='flex items-center gap-4'>
            <div className='h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary shadow-sm'>
              <Clock className='w-6 h-6' />
            </div>
            <div className='space-y-1'>
              <DialogTitle className='text-2xl font-bold text-foreground tracking-tight'>
                Time Sheet Analysis
              </DialogTitle>
              <p className='text-sm text-muted-foreground'>
                Review and analyze detailed time logs for this employee
              </p>
            </div>
          </div>
        </div>

        <div className='p-8 space-y-8 flex-1 overflow-hidden flex flex-col'>
          {/* CONTROLS BAR */}
          <div className='flex flex-col md:flex-row items-end gap-5 bg-muted/20 p-6 rounded-2xl border border-border/50 shadow-sm'>
            <div className='w-full md:flex-1 space-y-2'>
              <Label className='text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2 px-1'>
                <CalendarIcon className='w-3.5 h-3.5' />
                Start Date
              </Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant='outline'
                    className={cn(
                      'w-full justify-start text-left font-medium h-11 rounded-lg border-border bg-background hover:bg-muted/50 transition-all',
                      !startDate && 'text-muted-foreground'
                    )}
                  >
                    <CalendarIcon className='mr-2 h-4 w-4 opacity-50' />
                    {startDate ? (
                      format(new Date(startDate), 'PPP')
                    ) : (
                      <span>Pick a date</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className='w-auto p-0 rounded-xl border-border shadow-xl'
                  align='start'
                >
                  <Calendar
                    captionLayout='dropdown'
                    fromYear={2018}
                    mode='single'
                    selected={startDate ? new Date(startDate) : undefined}
                    onSelect={date =>
                      setStartDate(date ? format(date, 'yyyy-MM-dd') : '')
                    }
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className='w-full md:flex-1 space-y-2'>
              <Label className='text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2 px-1'>
                <CalendarIcon className='w-3.5 h-3.5' />
                End Date
              </Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant='outline'
                    className={cn(
                      'w-full justify-start text-left font-medium h-11 rounded-lg border-border bg-background hover:bg-muted/50 transition-all',
                      !endDate && 'text-muted-foreground'
                    )}
                  >
                    <CalendarIcon className='mr-2 h-4 w-4 opacity-50' />
                    {endDate ? (
                      format(new Date(endDate), 'PPP')
                    ) : (
                      <span>Pick a date</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className='w-auto p-0 rounded-xl border-border shadow-xl'
                  align='start'
                >
                  <Calendar
                    mode='single'
                    captionLayout='dropdown'
                    fromYear={2018}
                    selected={endDate ? new Date(endDate) : undefined}
                    onSelect={date =>
                      setEndDate(date ? format(date, 'yyyy-MM-dd') : '')
                    }
                    disabled={date =>
                      startDate ? new Date(date) < new Date(startDate) : false
                    }
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <Button
              onClick={handleSubmit}
              disabled={isLoading || !startDate || !endDate}
              className='w-full md:w-auto px-10 h-11 font-bold rounded-lg shadow-lg shadow-primary/10 transition-all'
            >
              {isLoading ? (
                <div className='flex items-center gap-2'>
                  <div className='w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin' />
                  <span>Syncing...</span>
                </div>
              ) : (
                'Fetch Timesheet'
              )}
            </Button>
          </div>

          {/* RESULT SECTION */}
          {showTable && (
            <div className='flex-1 flex flex-col overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-500'>
              <div className='flex items-center justify-between mb-4 px-1'>
                <div className='flex items-center gap-3'>
                  <h3 className='font-bold text-lg text-foreground'>
                    Data Overview
                  </h3>
                  <span className='px-2.5 py-1 rounded-full bg-primary/10 text-primary text-[11px] font-bold border border-primary/20 uppercase tracking-tight'>
                    {entries?.length ?? 0} Entries Found
                  </span>
                </div>
              </div>

              <div className='rounded-xl border border-border bg-card shadow-sm overflow-hidden flex flex-col flex-1'>
                <div className='overflow-auto flex-1'>
                  <Table>
                    <TableHeader className='bg-muted/50 sticky top-0 z-10 backdrop-blur-sm'>
                      <TableRow className='hover:bg-transparent border-b border-border'>
                        <TableHead className='px-4 py-4 font-bold text-foreground text-[11px] uppercase tracking-wider'>
                          <div className='flex items-center gap-2'>
                            <UserIcon className='w-3.5 h-3.5 text-muted-foreground' />{' '}
                            Employee
                          </div>
                        </TableHead>
                        <TableHead className='px-4 py-4 font-bold text-foreground text-[11px] uppercase tracking-wider'>
                          <div className='flex items-center gap-2'>
                            <FileText className='w-3.5 h-3.5 text-muted-foreground' />{' '}
                            Task Details
                          </div>
                        </TableHead>
                        <TableHead className='px-4 py-4 font-bold text-foreground text-[11px] uppercase tracking-wider'>
                          Duration
                        </TableHead>
                        <TableHead className='px-4 py-4 font-bold text-foreground text-[11px] uppercase tracking-wider text-center'>
                          Billing
                        </TableHead>
                        <TableHead className='px-4 py-4 font-bold text-foreground text-[11px] uppercase tracking-wider'>
                          Time Window
                        </TableHead>
                        <TableHead className='px-4 py-4 font-bold text-foreground text-[11px] uppercase tracking-wider text-right'>
                          <div className='flex items-center gap-2 justify-end'>
                            <TagIcon className='w-3.5 h-3.5 text-muted-foreground' />{' '}
                            Tags
                          </div>
                        </TableHead>
                      </TableRow>
                    </TableHeader>

                    <TableBody className='divide-y divide-border'>
                      {entries.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={6}
                            className='h-[40vh] text-center'
                          >
                            <div className='flex flex-col items-center justify-center gap-4 text-muted-foreground'>
                              <div className='p-6 bg-muted/50 rounded-full'>
                                <AlertCircle className='w-12 h-12 opacity-20' />
                              </div>
                              <div className='space-y-1'>
                                <p className='font-bold text-foreground text-lg'>
                                  No entries found
                                </p>
                                <p className='text-sm max-w-[250px] mx-auto'>
                                  No time logs were recorded for the selected
                                  date range.
                                </p>
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      ) : (
                        entries.map((entry: TimesheetEntry) => (
                          <TableRow
                            key={entry.id}
                            className='hover:bg-muted/30 transition-colors'
                          >
                            <TableCell className='px-4 py-4'>
                              <span className='font-bold text-foreground'>
                                {entry.user?.username ?? 'Unknown'}
                              </span>
                            </TableCell>

                            <TableCell className='px-4 py-4'>
                              <div className='max-w-[300px]'>
                                <p
                                  className='font-medium text-foreground text-sm line-clamp-2'
                                  title={entry.task?.name}
                                >
                                  {entry.task?.name ?? 'No task linked'}
                                </p>
                              </div>
                            </TableCell>

                            <TableCell className='px-4 py-4'>
                              <div className='inline-flex items-center px-2.5 py-1 rounded-md bg-muted font-mono font-bold text-xs border border-border shadow-sm'>
                                {formatDuration(entry.duration)}
                              </div>
                            </TableCell>

                            <TableCell className='px-4 py-4 text-center'>
                              {entry.billable ? (
                                <span className='inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-100/50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 text-[10px] font-bold border border-emerald-200/50 uppercase tracking-wider'>
                                  <CheckCircle2 className='w-3 h-3' />
                                  Billable
                                </span>
                              ) : (
                                <span className='inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-muted text-muted-foreground text-[10px] font-bold border border-border uppercase tracking-wider'>
                                  <XCircle className='w-3 h-3' />
                                  Non-Billable
                                </span>
                              )}
                            </TableCell>

                            <TableCell className='px-4 py-4'>
                              <div className='flex flex-col gap-0.5'>
                                <span className='text-[12px] font-semibold text-foreground'>
                                  {formatDateTime(entry.start)}
                                </span>
                                <span className='text-[10px] text-muted-foreground font-medium'>
                                  to {formatDateTime(entry.end)}
                                </span>
                              </div>
                            </TableCell>

                            <TableCell className='px-4 py-4 text-right'>
                              <div className='flex flex-wrap justify-end gap-1.5'>
                                {(entry.tags || []).length > 0 ? (
                                  entry.tags?.map((t: { name: string }) => (
                                    <span
                                      key={`tag-${t.name}`}
                                      className='bg-muted px-2 py-0.5 rounded text-[10px] font-bold text-muted-foreground border border-border shadow-sm'
                                    >
                                      {t.name}
                                    </span>
                                  ))
                                ) : (
                                  <span className='text-muted-foreground/30 text-xs italic'>
                                    —
                                  </span>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* FOOTER */}
        <div className='px-8 py-4 border-t border-border flex justify-end bg-muted/10'>
          <Button
            variant='outline'
            onClick={isClose}
            className='rounded-lg px-8 hover:bg-muted transition-all'
          >
            Close Viewer
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default EmloyeeMode
